import { ConfirmationActions, MeditatorPassFailActions, queryClient, SearchMeditatorActions } from "@/constants";
import { CLIENT_API } from "@/services/axios-client";
import { useNotificationStore } from "@/states/zustand/notification";
import { useMutation, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { useDebounce } from "../useDebounce";

export const useGetAllMeditators = (
    page: number,
    limit: number = 10,
    queryProps?: Omit<
        UseQueryOptions<
            PaginatedResponse<Meditator>,
            Error,
            PaginatedResponse<MeditatorWithMeditationRetreat>
        >,
        "queryKey" | "queryFn" | "select"
    >
) => {
    const { data, isLoading, ...rest } = useQuery<
        PaginatedResponse<Meditator>,
        Error,
        PaginatedResponse<MeditatorWithMeditationRetreat>
    >({
        queryKey: ["meditators", page, limit],
        queryFn: () => CLIENT_API.getAllMeditators(page, limit),
        select: (data) => ({
            ...data,
            content: data.content?.map((meditator) => ({
                user: meditator,
                events: [], // Assuming no event data is needed here
                event: undefined,
            })),
        }),
        ...queryProps,
    });

    return {
        data,
        isLoading,
        ...rest,
    }
}

export const useGetAddNewMeditator = () => {
    const { data, isPending, ...rest } = useMutation({
        mutationKey: ["add-new-meditator"],
        mutationFn: (payload: AddNewMeditatorRequest) => CLIENT_API.addNewMeditator(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["meditators"]
            })
            useNotificationStore.getState().setNotification({
                type: "success",
                message: "Meditator added successfully!"
            })
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                type: "error",
                message: error || "An error occurred while adding meditator."
            })
        },
        retry: 1,
    });

    return {
        data,
        isLoading: isPending,
        ...rest
    }
}

export const useUpdateMeditator = () => {
    const { data, isPending, ...rest } = useMutation({
        mutationKey: ["update-meditator"],
        mutationFn: (payload: { id: number, data: AddNewMeditatorRequest }) => CLIENT_API.updateMeditator(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["meditators"]
            })
            useNotificationStore.getState().setNotification({
                type: "success",
                message: "Meditator updated successfully!"
            })
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                type: "error",
                message: error || "An error occurred while updating meditator."
            })
        },
        retry: 1,
    });

    return {
        data,
        isLoading: isPending,
        ...rest
    }
}

export const useManualRegisterMeditator = () => {

    const { mutate, isPending, ...rest } = useMutation({
        mutationKey: ["manual-register-meditator"],
        mutationFn: (data: ManualRegisterMeditatorRequest) => CLIENT_API.manualRegisterMeditator(data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["pending-meditators", "meditation-retreat", "meditators-by-retreat", "statistics"]
            })
            useNotificationStore.getState().setNotification({
                type: "success",
                message: "Meditator registered successfully!"
            })
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                type: "error",
                message: error || "An error occurred while registering meditator."
            })
        }
    })

    return {
        mutate,
        isLoading: isPending,
        ...rest
    }

}

export const useGetRegistrationPendingMeditators = (page: number, limit?: number) => {
    const { data, isLoading, ...rest } = useQuery({
        queryKey: ["pending-meditators", page],
        queryFn: () => CLIENT_API.getRegistrationPendingMeditators(page, limit),
        select: (data) => {
            const content = data.content?.map((el) => ({
                ...el,
                event: el.events?.[0] ?? undefined, // Assuming the first event is the one we want
            }));

            return {
                ...data,
                content
            }
        }
    });

    return {
        data,
        isLoading,
        ...rest
    }
}

export const useGetMeditatorsByMeditationRetreat = (retreatId: string, page: number, status: "ACCEPTED" | "PENDING" = "ACCEPTED", limit: number = 10) => {

    const { data, isLoading, ...rest } = useQuery({
        queryKey: ["meditators-by-retreat", retreatId, page, status, limit],
        queryFn: () => CLIENT_API.getMeditatorsByMeditationRetreat({ retreatId, page, status, limit }),
        enabled: !!retreatId,
        refetchOnMount: true,
        select: (data) => {
            const content = data.content?.map((meditator) => ({
                user: meditator,
                event: undefined
            }) as MeditatorWithMeditationRetreat);

            return {
                ...data,
                content
            }
        }
    });

    return {
        data,
        isLoading,
        ...rest
    }
}

export const useAcceptRejectRegistrationPendingMeditators = () => {
    const { mutate, isPending, ...rest } = useMutation({
        mutationKey: ["accept-reject-registration-pending-meditators"],
        mutationFn: ({ ids, action }: { ids: number[], action: ConfirmationActions }) => {
            switch (action) {
                case ConfirmationActions.ACCEPT:
                    return CLIENT_API.acceptRegisteredMeditator(ids);
                case ConfirmationActions.REJECT:
                    return CLIENT_API.rejectRegisteredMeditator(ids);
            }
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({
                queryKey: ["pending-meditators"]
            })
            useNotificationStore.getState().setNotification({
                type: "success",
                message: data?.message || "Meditators accepted/rejected successfully!"
            })
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                type: "error",
                message: error || "An error occurred while accepting/rejecting meditators."
            })
        },
        retry: 1,
    });

    return {
        mutate,
        isLoading: isPending,
        ...rest
    }
}

// cancellation request meditators
export const useGetCancellationRequestedMeditators = (page: number) => {
    const { data, isLoading, ...rest } = useQuery({
        queryKey: ["cancellation-requested-meditators", page],
        queryFn: () => CLIENT_API.getCancellationRequestedMeditators(page),
        select: (data) => {
            const content = data.content?.map((el) => ({
                ...el,
                event: el.events?.[0] ?? undefined, // Assuming the first event is the one we want
            }));

            return {
                ...data,
                content
            }
        }
    });

    return {
        data,
        isLoading,
        ...rest
    }
}

export const useDeleteUser = () => {
    const { mutate, isPending, ...rest } = useMutation({
        mutationKey: ["delete-meditator"],
        mutationFn: (id: number) => CLIENT_API.deleteUser(id),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["meditators"]
            })

            useNotificationStore.getState().setNotification({
                type: "success",
                message: "User deleted successfully!"
            })
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                type: "error",
                message: error || "An error occurred while deleting meditator."
            })
        }
    });

    return {
        mutate,
        isMutating: isPending,
        ...rest
    }
}

export const useConfirmRejectCancellationRequest = () => {

    const { mutate, isPending, ...rest } = useMutation({
        mutationKey: ["confirm-reject-cancellation-request"],
        mutationFn: ({ ids, action }: { ids: number[], action: ConfirmationActions }) => {
            switch (action) {
                case ConfirmationActions.ACCEPT:
                    return CLIENT_API.confirmCancellationRequest(ids);
                case ConfirmationActions.REJECT:
                    return CLIENT_API.rejectCancellationRequest(ids);
            }
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({
                queryKey: ["cancellation-requested-meditators"]
            })
            useNotificationStore.getState().setNotification({
                type: "success",
                message: data?.message || "Cancellation request processed successfully!"
            })
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                type: "error",
                message: error || "An error occurred while processing cancellation request."
            })
        }
    });

    return {
        mutate,
        isLoading: isPending,
        ...rest
    }

}

export const useManualRequestCancellation = () => {

    const { mutate, isPending, ...rest } = useMutation({
        mutationKey: ["manual-request-cancellation"],
        mutationFn: ({ retreatId, data }: { retreatId: number, data: Partial<Meditator> }) => CLIENT_API.manualRequestCancellation(retreatId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["cancellation-requested-meditators"]
            })
            useNotificationStore.getState().setNotification({
                type: "success",
                message: "Cancellation request sent successfully!"
            })
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                type: "error",
                message: error || "An error occurred while sending cancellation request."
            })
        }
    });

    return {
        mutate,
        isLoading: isPending,
        ...rest
    }
}

// search
export const useSearchMeditators = (keyword: string | null) => {

    const debouncedValue = useDebounce(keyword!, 500)

    const { data, isLoading, ...rest } = useQuery<
        Meditator[],
        Error,
        PaginatedResponse<MeditatorWithMeditationRetreat>
    >({
        queryKey: ["search-meditators", debouncedValue],
        queryFn: () => CLIENT_API.searchMeditators(debouncedValue),
        enabled: !!debouncedValue, // Only run the query if keyword is not empty
        select: (data) => {
            const content = data?.map((meditator) => ({
                user: meditator,
                event: undefined // Assuming no event data is needed here
            }) as MeditatorWithMeditationRetreat);

            return {
                content,
                page: 0,
                size: content?.length ?? 0,
                totalPages: content?.length > 10 ? Math.ceil(content.length / 10) : 1,
                totalElements: content?.length ?? 0
            }
        }
    });

    return {
        data,
        isLoading,
        ...rest
    }
}

export const useSearchMeditatorByPhoneNumber = (phoneNumber: string, action: SearchMeditatorActions) => {

    const debouncedValue = useDebounce(phoneNumber, 500);

    const { data, isLoading, ...rest } = useQuery({
        queryKey: ["search-meditators-by-phone", debouncedValue],
        queryFn: () => CLIENT_API.searchMeditatorByPhoneNumber(debouncedValue, action),
        enabled: !!debouncedValue, // Only run the query if phoneNumber is not empty
        staleTime: 1000 * 60 * 5, // 1 minute
    });

    return {
        data,
        isLoading,
        ...rest
    }
}

// search with user name/bookId & event id
export const useSearchMeditatorByKeywordAndEventId = (keyword: string | null, eventId: number) => {
    return useQuery({
        queryKey: ['search-by-keyword-and-event', keyword, eventId],
        queryFn: () => CLIENT_API.searchMeditatorByKeywordAndEvent(keyword!, eventId),
        enabled: !!keyword && !!eventId,
    })
}

export const useGetUserList = (
    page: number,
    limit: number = 10,
    queryProps?: Omit<
        UseQueryOptions<
            PaginatedResponse<Meditator>,
            Error,
            PaginatedResponse<MeditatorWithMeditationRetreat>
        >,
        "queryKey" | "queryFn" | "select"
    >
) => {
    const { data, isLoading, ...rest } = useQuery({
        queryKey: ["meditators", page, limit],
        queryFn: () => CLIENT_API.getUserList(page, limit),
        select: (data) => {
            const content = data.content?.map((user) => ({
                user,
                events: [], // Assuming no event data is needed here
                event: undefined,
            }));

            return {
                ...data,
                content
            }
        },
        ...queryProps
    });

    return {
        data,
        isLoading,
        ...rest
    }
}

export const usePassAndFailMeditatorsToRetreat = () => {

    const { mutate, isPending, ...rest } = useMutation({
        mutationKey: ["pass-fail-meditators-to-retreat"],
        mutationFn: ({ retreatId, data, action }: { retreatId: number, data: FormData, action: MeditatorPassFailActions }) => {
            switch (action) {
                case MeditatorPassFailActions.PASS:
                    return CLIENT_API.passMeditatorsToRetreat(retreatId, data);
                case MeditatorPassFailActions.FAIL:
                    return CLIENT_API.failMeditatorsToRetreat(retreatId, data);
            }
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({
                queryKey: ["meditators-by-retreat"]
            })
            useNotificationStore.getState().setNotification({
                type: "success",
                message: data?.message || "Meditators processed successfully!"
            })
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                type: "error",
                message: error || "An error occurred while processing meditators."
            })
        }
    });

    return {
        mutate,
        isMutating: isPending,
        ...rest
    }
}

export const useGetRoomUnassignedMeditators = (retreatId: number, isEnabled: boolean = true) => {
    return useQuery({
        queryKey: ['get-room-unassigned-meditators', retreatId],
        queryFn: () => CLIENT_API.getRoomUnassignedMeditators(retreatId),
        enabled: !!retreatId && isEnabled
    })
}